import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { DepartureCheckService } from 'src/app/services/departure-check.service';
import { UnitService } from 'src/app/services/unit.service';
import { DriverService } from 'src/app/services/driver.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
    selector: 'app-departure-check-dashboard',
    templateUrl: './departure-check-dashboard.component.html',
    styleUrls: ['./departure-check-dashboard.component.css']
})
export class DepartureCheckDashboardComponent implements OnInit {

    stats = { pendientes: 0, ruta_a_carga: 0, total: 0 };
    rows: any[] = [];
    units: any[] = [];
    drivers: any[] = [];

    filterFolio = '';
    filterUnitId = '';
    filterDriverId = '';
    filterDepartureDate = '';

    currentPage = 1;
    lastPage = 1;
    perPage = 5;
    total = 0;

    loading = false;

    /** Panel de filtros inferior (tablet angosta): se abre con el boton
     * "Filtrar productos" y usa un borrador para no aplicar cambios hasta
     * pulsar "Aplicar filtros" (igual que el diseño de Figma). */
    showFilterDrawer = false;
    draftFolio = '';
    draftUnitId = '';
    draftDriverId = '';
    draftDepartureDate = '';

    constructor(
        private departureCheckService: DepartureCheckService,
        private unitService: UnitService,
        private driverService: DriverService,
        private router: Router,
        private spinner: NgxSpinnerService,
        private utils: UtilsService
    ) { }

    ngOnInit(): void {
        this.unitService.getInfo().subscribe({ next: (resp: any) => this.units = Array.isArray(resp) ? resp : (resp?.data || []) });
        this.driverService.getInfo().subscribe({ next: (resp: any) => this.drivers = Array.isArray(resp) ? resp : (resp?.data || []) });
        this.fetch();
    }

    fetch(): void {
        this.loading = true;
        this.spinner.show();
        const params: any = { page: this.currentPage, per_page: this.perPage };
        if (this.filterFolio) params.folio = this.filterFolio;
        if (this.filterUnitId) params.unit_id = this.filterUnitId;
        if (this.filterDriverId) params.driver_id = this.filterDriverId;
        if (this.filterDepartureDate) params.departure_date = this.filterDepartureDate;

        this.departureCheckService.listPending(params).subscribe({
            next: (resp: any) => {
                this.stats = resp?.stats || this.stats;
                this.rows = resp?.data || [];
                this.currentPage = resp?.pagination?.current_page || 1;
                this.lastPage = resp?.pagination?.last_page || 1;
                this.total = resp?.pagination?.total || 0;
                this.perPage = resp?.pagination?.per_page || this.perPage;
                this.loading = false;
                this.spinner.hide();
            },
            error: () => {
                this.loading = false;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el listado de unidades por liberar', icon: 'error' });
            }
        });
    }

    onFilterChanged(): void {
        this.currentPage = 1;
        this.fetch();
    }

    openFilterDrawer(): void {
        this.draftFolio = this.filterFolio;
        this.draftUnitId = this.filterUnitId;
        this.draftDriverId = this.filterDriverId;
        this.draftDepartureDate = this.filterDepartureDate;
        this.showFilterDrawer = true;
    }

    closeFilterDrawer(): void {
        this.showFilterDrawer = false;
    }

    applyFilterDrawer(): void {
        this.filterFolio = this.draftFolio;
        this.filterUnitId = this.draftUnitId;
        this.filterDriverId = this.draftDriverId;
        this.filterDepartureDate = this.draftDepartureDate;
        this.showFilterDrawer = false;
        this.onFilterChanged();
    }

    goToPage(page: number): void {
        if (page < 1 || page > this.lastPage) return;
        this.currentPage = page;
        this.fetch();
    }

    /** Ventana de a lo mas 3 botones de pagina (Figma: "1 2 3"), centrada
     * en la pagina actual y recortada en los extremos. */
    get pageNumbers(): number[] {
        const windowSize = 3;
        const end = Math.min(this.lastPage, Math.max(1, this.currentPage - 1) + windowSize - 1);
        const start = Math.max(1, end - windowSize + 1);
        const pages: number[] = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }

    /** "Mostrando X a Y de Z unidades" (Figma: pie de la tabla). */
    get paginationRangeLabel(): string {
        if (this.total === 0) return 'Mostrando 0 unidades';
        const start = (this.currentPage - 1) * this.perPage + 1;
        const end = Math.min(this.currentPage * this.perPage, this.total);
        return `Mostrando ${start} a ${end} de ${this.total} unidades`;
    }

    rowActionLabel(row: any): string {
        if (row.departure_check_status === 'completed') return 'Ver resumen';
        if (row.departure_check_status === 'in_progress') return 'Continuar check';
        return 'Iniciar check';
    }

    rowStateLabel(row: any): string {
        if (row.departure_check_status === 'completed') return 'Liberada';
        if (row.departure_check_status === 'in_progress') return 'En progreso';
        if (row.departure_check_status === 'update_requested') return 'En revisión';
        return 'Pendiente';
    }

    rowStateIcon(row: any): string {
        if (row.departure_check_status === 'completed') return 'assets/img/departure-check/icon-kpi-check.svg';
        if (row.departure_check_status === 'in_progress') return 'assets/img/departure-check/icon-kpi-clock-blue.svg';
        if (row.departure_check_status === 'update_requested') return 'assets/img/departure-check/icon-file-search-purple.svg';
        return 'assets/img/departure-check/icon-kpi-clock.svg';
    }

    rowStateClass(row: any): string {
        if (row.departure_check_status === 'completed') return 'departure-status--done';
        if (row.departure_check_status === 'in_progress') return 'departure-status--progress';
        if (row.departure_check_status === 'update_requested') return 'departure-status--review';
        return 'departure-status--pending';
    }

    onRowAction(row: any): void {
        if (row.departure_check_status === 'completed') {
            this.router.navigateByUrl(`/unit/departure-checks/${row.departure_check_id}/resumen`);
            return;
        }
        if (row.departure_check_status === 'in_progress' && row.departure_check_id) {
            this.router.navigate(['/unit/departure-checks/wizard', row.departure_check_id], { queryParams: { resume: 1 } });
            return;
        }
        if (row.departure_check_status === 'update_requested' && row.departure_check_id) {
            this.router.navigateByUrl(`/unit/departure-checks/${row.departure_check_id}/resumen`);
            return;
        }
        this.spinner.show();
        this.departureCheckService.startCheck(row.service_id).subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                this.router.navigateByUrl(`/unit/departure-checks/wizard/${resp.id}`);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo iniciar el check de salida', icon: 'error' });
            }
        });
    }

    formatDateTime(date: string, time: string): { date: string, time: string } {
        if (!date) return { date: '--/--/----', time: '--:--' };
        const [yyyy, mm, dd] = date.split('T')[0].split('-');
        return { date: `${dd}/${mm}/${yyyy}`, time: (time || '').substring(0, 5) };
    }
}
